// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetBody, SheetFooter } from './ui/sheet';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AdvancedDataTableV2, ColumnDef, BulkAction } from './advanced-data-table-v2';
import { 
  Plus, 
  Building2, 
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Activity,
  Loader2,
  TrendingUp,
  AlertCircle,
  User
} from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { supabase, type Client } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { hasPermission, type UserRole } from '@product-radar/shared';
import { formatCurrency, formatDate } from '@/lib/formatting';
import { validateClientNumericFields } from '@/lib/client-validation';

interface ClientManagementProps {
  onViewDetails?: (clientId: string) => void;
}

export function ClientManagement({ onViewDetails }: ClientManagementProps) {
  const { workspace, membership } = useWorkspace();
  const userRole = membership?.role as UserRole;
  const regionalSettings = useRegionalSettings();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  // Workspace members for account owner lookup
  const [workspaceMembers, setWorkspaceMembers] = useState<{ user_id: string; display_name: string; email: string }[]>([]);

  // Form state for new client
  const [newClient, setNewClient] = useState({
    name: '',
    industry: '',
    size: '',
    status: 'active' as 'active' | 'inactive' | 'churned',
    arr: '',
    health_score: '',
    primary_contact: '',
    account_owner: '',
    account_owner_user_id: null as string | null,
    notes: ''
  });

  // Form state for editing client
  const [editForm, setEditForm] = useState({
    name: '',
    industry: '',
    size: '',
    status: 'active' as 'active' | 'inactive' | 'churned',
    arr: '',
    health_score: '',
    primary_contact: '',
    account_owner: '',
    account_owner_user_id: null as string | null
  });

  useEffect(() => {
    if (workspace) {
      loadClients();
      loadWorkspaceMembers();
    }
  }, [workspace]);

  const loadWorkspaceMembers = async () => {
    if (!workspace) return;
    try {
      const { data, error } = await supabase.rpc('get_workspace_member_names_for_lookup', {
        p_workspace_id: workspace.id
      });
      if (error) throw error;
      setWorkspaceMembers(data || []);
    } catch (err) {
      console.error('Error loading workspace members:', err);
    }
  };

  const loadClients = async () => {
    if (!workspace) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!workspace) {
      toast.error('Workspace not loaded');
      return;
    }
    
    if (!newClient.name) {
      toast.error('Please enter a client name');
      return;
    }

    const { arr: parsedArr, health_score: parsedHealth, error: validationError } = validateClientNumericFields(newClient.arr, newClient.health_score);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const { data, error} = await supabase
        .from('clients')
        .insert({
          workspace_id: workspace.id,
          name: newClient.name,
          industry: newClient.industry || null,
          size: newClient.size || null,
          status: newClient.status,
          arr: parsedArr,
          health_score: parsedHealth,
          primary_contact: newClient.primary_contact || null,
          account_owner: newClient.account_owner || null,
          account_owner_user_id: newClient.account_owner_user_id || null,
          notes: newClient.notes || null
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Client created successfully!');
      setDialogOpen(false);
      setNewClient({
        name: '',
        industry: '',
        size: '',
        status: 'active',
        arr: '',
        health_score: '',
        primary_contact: '',
        account_owner: '',
        account_owner_user_id: null,
        notes: ''
      });
      
      loadClients();
    } catch (error: any) {
      console.error('Error creating client:', error);
      toast.error('Failed to create client', {
        description: error.message
      });
    }
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setEditForm({
      name: client.name,
      industry: client.industry || '',
      size: client.size || '',
      status: (client.status || 'active') as 'active' | 'inactive' | 'churned',
      arr: client.arr ? String(client.arr) : '',
      health_score: client.health_score ? String(client.health_score) : '',
      primary_contact: client.primary_contact || '',
      account_owner: client.account_owner || '',
      account_owner_user_id: client.account_owner_user_id || null
    });
    setEditDialogOpen(true);
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;

    const { arr: parsedArr, health_score: parsedHealth, error: validationError } = validateClientNumericFields(editForm.arr, editForm.health_score);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const { error } = await supabase
        .from('clients')
        .update({
          name: editForm.name,
          industry: editForm.industry || null,
          size: editForm.size || null,
          status: editForm.status,
          arr: parsedArr,
          health_score: parsedHealth,
          primary_contact: editForm.primary_contact || null,
          account_owner: editForm.account_owner || null,
          account_owner_user_id: editForm.account_owner_user_id || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingClient.id);

      if (error) throw error;

      toast.success('Client updated successfully');
      setEditDialogOpen(false);
      setEditingClient(null);
      loadClients();
    } catch (error: any) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client', {
        description: error.message
      });
    }
  };

  const handleDeleteClient = async (id: string) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Client deleted successfully');
      loadClients();
    } catch (error: any) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client', {
        description: error.message
      });
    }
  };

  const handleBulkAction = async (action: string, selectedIds: (string | number)[]) => {
    try {
      if (action === 'delete') {
        const { error } = await supabase
          .from('clients')
          .delete()
          .in('id', selectedIds);

        if (error) throw error;

        toast.success(`Deleted ${selectedIds.length} clients`);
        loadClients();
      } else if (action === 'activate') {
        const { error } = await supabase
          .from('clients')
          .update({ status: 'active' })
          .in('id', selectedIds);

        if (error) throw error;

        toast.success(`Activated ${selectedIds.length} clients`);
        loadClients();
      } else {
        toast.success(`${action} applied to ${selectedIds.length} clients`);
      }
    } catch (error: any) {
      console.error(`Error performing bulk action:`, error);
      toast.error(`Failed to ${action} clients`, {
        description: error.message
      });
    }
  };

  const handleRefresh = () => {
    loadClients();
    toast.success('Client data refreshed!');
  };

  const handleExport = () => {
    const headers = ['Name', 'Industry', 'Size', 'Status', 'ARR', 'Health Score', 'Contact', 'Account Owner'];
    const csvContent = [
      headers.join(','),
      ...clients.map(client => [
        `"${client.name}"`,
        client.industry || '',
        client.size || '',
        client.status,
        client.arr || '',
        client.health_score || '',
        `"${client.primary_contact || ''}"`,
        `"${client.account_owner || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    toast.success('Client data exported successfully!');
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      'active': 'bg-green-50 text-green-600 border-green-200',
      'inactive': 'bg-gray-50 text-gray-600 border-gray-200',
      'churned': 'bg-red-50 text-red-600 border-red-200'
    };
    return styles[status as keyof typeof styles] || styles.active;
  };

  const getHealthScoreColor = (score?: number) => {
    if (!score) return 'bg-gray-400';
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Table configuration
  const columns: ColumnDef<Client>[] = [
    {
      key: 'name',
      label: 'Client Name',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'search',
          key: 'name',
          label: 'Search',
          placeholder: 'Search client name...'
        }
      ],
      width: 'min-w-[250px]',
      render: (client) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{client.name}</span>
          </div>
          {client.primary_contact && (
            <div className="text-sm text-muted-foreground">
              {client.primary_contact}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'industry',
      label: 'Industry',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'industry',
          label: 'Industry',
          options: [
            { value: 'technology', label: 'Technology' },
            { value: 'saas', label: 'SaaS' },
            { value: 'manufacturing', label: 'Manufacturing' },
            { value: 'research', label: 'Research' },
            { value: 'creative', label: 'Creative' }
          ]
        }
      ],
      render: (client) => (
        <Badge variant="outline">{client.industry || 'N/A'}</Badge>
      )
    },
    {
      key: 'size',
      label: 'Size',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'size',
          label: 'Company Size',
          options: [
            { value: 'small', label: 'Small' },
            { value: 'mid-market', label: 'Mid-Market' },
            { value: 'enterprise', label: 'Enterprise' }
          ]
        }
      ],
      render: (client) => (
        <span className="text-sm">{client.size || 'N/A'}</span>
      )
    },
    {
      key: 'arr',
      label: 'ARR',
      sortable: true,
      defaultVisible: false,
      render: (client) => (
        <span className="font-medium">
          {client.arr ? formatCurrency(client.arr, regionalSettings) : 'N/A'}
        </span>
      )
    },
    {
      key: 'health_score',
      label: 'Health',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'health_score',
          label: 'Health Score',
          options: [
            { value: '80-100', label: 'Excellent (80-100)' },
            { value: '60-79', label: 'Good (60-79)' },
            { value: '0-59', label: 'At Risk (0-59)' }
          ]
        }
      ],
      render: (client) => {
        if (!client.health_score) return <span className="text-sm text-muted-foreground">N/A</span>;
        return (
          <div className="flex items-center space-x-2">
            <div className={`h-2 w-2 rounded-full ${getHealthScoreColor(client.health_score)}`} />
            <span className="font-medium">{client.health_score}%</span>
          </div>
        );
      }
    },
    {
      key: 'account_owner',
      label: 'Account Owner',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'search',
          key: 'account_owner',
          label: 'Account Owner',
          placeholder: 'Search account owner...'
        }
      ],
      defaultVisible: true,
      render: (client) => (
        <span className="text-sm">{client.account_owner || '—'}</span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'status',
          label: 'Status',
          options: [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'churned', label: 'Churned' }
          ]
        }
      ],
      render: (client) => (
        <Badge className={getStatusBadge(client.status)}>
          {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
        </Badge>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'dateRange',
          key: 'created_at',
          label: 'Created Date'
        }
      ],
      render: (client) => (
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(client.created_at, regionalSettings)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: '',
      width: 'w-[50px]',
      render: (client) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewDetails?.(client.id)}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {hasPermission(userRole, 'canUpdateClients') && (
              <DropdownMenuItem onClick={() => handleEditClient(client)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            )}
            {hasPermission(userRole, 'canDeleteClients') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-destructive"
                  onClick={() => handleDeleteClient(client.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const bulkActions: BulkAction[] = [
    ...(hasPermission(userRole, 'canUpdateClients') ? [{
      key: 'activate',
      label: 'Mark Active',
      icon: <Activity className="h-4 w-4 mr-2" />,
      variant: 'outline' as const
    }] : []),
    ...(hasPermission(userRole, 'canDeleteClients') ? [{
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4 mr-2" />,
      variant: 'destructive' as const,
      requireConfirmation: true,
      confirmTitle: 'Delete Selected Clients',
      confirmDescription: 'Are you sure you want to delete the selected clients? This action cannot be undone.'
    }] : [])
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 min-w-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold">Client Management</h1>
          <p className="text-muted-foreground">Manage customer accounts and track engagement</p>
        </div>
        {hasPermission(userRole, 'canCreateClients') && (
          <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
            <SheetTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Add New Client</SheetTitle>
                <SheetDescription>
                  Create a new client account in the system
                </SheetDescription>
              </SheetHeader>
              
              <SheetBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Client Name *</Label>
                      <Input 
                        id="name" 
                        placeholder="Company name" 
                        value={newClient.name}
                        onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="primary_contact">Primary Contact</Label>
                      <Input 
                        id="primary_contact" 
                        placeholder="Contact name" 
                        value={newClient.primary_contact}
                        onChange={(e) => setNewClient({ ...newClient, primary_contact: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Account Owner</Label>
                    <div className="flex gap-1">
                      <Input
                        placeholder="Type name or select from workspace..."
                        value={newClient.account_owner}
                        onChange={(e) => setNewClient({ ...newClient, account_owner: e.target.value, account_owner_user_id: null })}
                        className="flex-1"
                      />
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" size="icon" title="Select from workspace members">
                            <User className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="end">
                          <Command>
                            <CommandInput placeholder="Search members..." />
                            <CommandEmpty>No members found.</CommandEmpty>
                            <CommandGroup>
                              {workspaceMembers.map((m) => (
                                <CommandItem
                                  key={m.user_id}
                                  value={`${m.display_name} ${m.email}`}
                                  onSelect={() => setNewClient({ ...newClient, account_owner: m.display_name || m.email, account_owner_user_id: m.user_id })}
                                >
                                  <User className="h-4 w-4 mr-2" />
                                  <span>{m.display_name || m.email}</span>
                                  {m.email && m.display_name && (
                                    <span className="text-xs text-muted-foreground ml-1">({m.email})</span>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry</Label>
                      <Input 
                        id="industry" 
                        placeholder="e.g., Technology, SaaS" 
                        value={newClient.industry}
                        onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="size">Company Size</Label>
                      <Select value={newClient.size} onValueChange={(value) => setNewClient({ ...newClient, size: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Small">Small</SelectItem>
                          <SelectItem value="Mid-Market">Mid-Market</SelectItem>
                          <SelectItem value="Enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                    <Label htmlFor="arr">
                      {`ARR (${(regionalSettings.currency || 'USD').toUpperCase()})`}
                    </Label>
                      <Input 
                        id="arr" 
                        type="number" 
                        placeholder="25000" 
                        value={newClient.arr}
                        onChange={(e) => setNewClient({ ...newClient, arr: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="health_score">Health Score (0-100)</Label>
                      <Input 
                        id="health_score" 
                        type="number" 
                        min="0" 
                        max="100" 
                        placeholder="85" 
                        value={newClient.health_score}
                        onChange={(e) => setNewClient({ ...newClient, health_score: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select value={newClient.status} onValueChange={(value: any) => setNewClient({ ...newClient, status: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="churned">Churned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea 
                      id="notes" 
                      placeholder="Additional notes about this client..." 
                      rows={3}
                      value={newClient.notes}
                      onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                    />
                  </div>
                </div>
              </SheetBody>
              
              <SheetFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateClient}>Create Client</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        )}
      </div>

      {/* Advanced Data Table */}
      <AdvancedDataTableV2
        title="Clients"
        description="All client accounts in the system"
        data={clients}
        columns={columns}
        bulkActions={bulkActions}
        pageSize={50}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onBulkAction={handleBulkAction}
        onRowClick={(item) => onViewDetails && onViewDetails(item.id)}
        emptyStateTitle="No clients found"
        emptyStateDescription="Get started by adding your first client"
        emptyStateIcon={<Building2 className="h-8 w-8" />}
      />

      {/* Edit Dialog */}
      <Sheet open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Client</SheetTitle>
            <SheetDescription>Update client details</SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Client Name *</Label>
                <Input 
                  id="edit-name" 
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-industry">Industry</Label>
                  <Input 
                    id="edit-industry" 
                    value={editForm.industry}
                    onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-size">Company Size</Label>
                  <Select value={editForm.size} onValueChange={(value) => setEditForm({ ...editForm, size: value })}>
                    <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Small">Small</SelectItem>
                      <SelectItem value="Mid-Market">Mid-Market</SelectItem>
                      <SelectItem value="Enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select value={editForm.status} onValueChange={(value: any) => setEditForm({ ...editForm, status: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="churned">Churned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-arr">
                    {`ARR (${(regionalSettings.currency || 'USD').toUpperCase()})`}
                  </Label>
                  <Input 
                    id="edit-arr" 
                    type="number" 
                    value={editForm.arr}
                    onChange={(e) => setEditForm({ ...editForm, arr: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-health">Health Score</Label>
                  <Input 
                    id="edit-health" 
                    type="number" 
                    min="0" 
                    max="100"
                    value={editForm.health_score}
                    onChange={(e) => setEditForm({ ...editForm, health_score: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-contact">Primary Contact</Label>
                <Input 
                  id="edit-contact" 
                  value={editForm.primary_contact}
                  onChange={(e) => setEditForm({ ...editForm, primary_contact: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Account Owner</Label>
                <div className="flex gap-1">
                  <Input
                    placeholder="Type name or select from workspace..."
                    value={editForm.account_owner}
                    onChange={(e) => setEditForm({ ...editForm, account_owner: e.target.value, account_owner_user_id: null })}
                    className="flex-1"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="icon" title="Select from workspace members">
                        <User className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="end">
                      <Command>
                        <CommandInput placeholder="Search members..." />
                        <CommandEmpty>No members found.</CommandEmpty>
                        <CommandGroup>
                          {workspaceMembers.map((m) => (
                            <CommandItem
                              key={m.user_id}
                              value={`${m.display_name} ${m.email}`}
                              onSelect={() => setEditForm({ ...editForm, account_owner: m.display_name || m.email, account_owner_user_id: m.user_id })}
                            >
                              <User className="h-4 w-4 mr-2" />
                              <span>{m.display_name || m.email}</span>
                              {m.email && m.display_name && (
                                <span className="text-xs text-muted-foreground ml-1">({m.email})</span>
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateClient}>Save Changes</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}